import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Payment, PaymentStatus, PaymentMethod, ApiResponse, StudentPaymentDetails } from '../../types/portal';
import { apiService } from '../../services/api';
import { API_ENDPOINTS } from '../../constants/apiEndpoints';

interface PaymentState {
  payments: Payment[];
  studentPayments: Payment[];
  paymentDetails: StudentPaymentDetails | null;
  isLoading: boolean;
  uploadingProof: boolean;
  error: string | null;
}

// Mock API functions - replace with actual API calls
const mockGetPayments = async (): Promise<ApiResponse<Payment[]>> => {
  await new Promise(resolve => setTimeout(resolve, 500));
  
  const mockPayments: Payment[] = [
    {
      id: '1',
      studentId: '1',
      amount: 150.00,
      currency: 'EUR',
      status: PaymentStatus.VERIFIED,
      method: PaymentMethod.BANK_TRANSFER,
      description: 'Driving lesson package (10 hours)',
      proofDocument: 'https://example.com/payment-proof-1.pdf',
      createdAt: '2024-09-15T00:00:00Z',
      verifiedAt: '2024-09-16T10:30:00Z',
      verifiedBy: '3',
    },
    {
      id: '2',
      studentId: '1',
      amount: 75.50,
      currency: 'EUR',
      status: PaymentStatus.PENDING,
      method: PaymentMethod.BANK_TRANSFER,
      description: 'Additional practice session',
      proofDocument: 'https://example.com/payment-proof-2.pdf',
      createdAt: '2024-09-20T00:00:00Z',
    },
    {
      id: '3',
      studentId: '4',
      amount: 200.00,
      currency: 'EUR',
      status: PaymentStatus.VERIFIED,
      method: PaymentMethod.CARD,
      description: 'Theory and practical lessons combo',
      proofDocument: 'https://example.com/payment-proof-3.pdf',
      createdAt: '2024-09-18T00:00:00Z',
      verifiedAt: '2024-09-19T14:15:00Z',
      verifiedBy: '3',
    },
    {
      id: '4',
      studentId: '4',
      amount: 45.00,
      currency: 'EUR',
      status: PaymentStatus.REJECTED,
      method: PaymentMethod.CASH,
      description: 'Exam preparation session',
      createdAt: '2024-09-25T00:00:00Z',
      verifiedAt: '2024-09-26T09:00:00Z',
      verifiedBy: '3',
    },
  ];

  return {
    success: true,
    data: mockPayments,
  };
};

const mockGetStudentPayments = async (studentId: string): Promise<ApiResponse<Payment[]>> => {
  await new Promise(resolve => setTimeout(resolve, 500));
  
  const response = await mockGetPayments();
  const studentPayments = response.data?.filter(payment => payment.studentId === studentId) || [];
  
  return {
    success: true,
    data: studentPayments,
  };
};

const mockCreatePayment = async (paymentData: Omit<Payment, 'id' | 'createdAt'>): Promise<ApiResponse<Payment>> => {
  await new Promise(resolve => setTimeout(resolve, 500));
  
  const newPayment: Payment = {
    ...paymentData,
    id: Math.random().toString(36).substr(2, 9),
    createdAt: new Date().toISOString(),
  };

  return {
    success: true,
    data: newPayment,
    message: 'Payment proof uploaded successfully',
  };
};

const mockUploadProofDocument = async (file: File): Promise<ApiResponse<string>> => {
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  // Mock file validation
  if (file.type !== 'application/pdf') {
    return {
      success: false,
      error: 'Only PDF files are allowed',
    };
  }
  
  if (file.size > 5 * 1024 * 1024) { // 5MB limit
    return {
      success: false,
      error: 'File size must be less than 5MB',
    };
  }
  
  // Mock file URL
  const mockFileUrl = `https://example.com/uploads/${file.name}-${Date.now()}.pdf`;
  
  return {
    success: true,
    data: mockFileUrl,
    message: 'File uploaded successfully',
  };
};

const mockVerifyPayment = async (paymentId: string, status: PaymentStatus, verifiedBy: string): Promise<ApiResponse<void>> => {
  await new Promise(resolve => setTimeout(resolve, 500));
  
  return {
    success: true,
    message: `Payment ${status === PaymentStatus.VERIFIED ? 'verified' : 'rejected'} successfully`,
  };
};

// Async thunks
export const fetchPayments = createAsyncThunk(
  'payment/fetchPayments',
  async (_, { rejectWithValue }) => {
    try {
      const resp = await apiService.get<any>('/super-admin/api/payments');
      const list = Array.isArray(resp?.payments) ? resp.payments : Array.isArray(resp?.data) ? resp.data : [];
      const mapped: Payment[] = list.map((p: any) => ({
        id: String(p.id),
        studentId: String(p.student?.id ?? p.studentId ?? ''),
        amount: Number(p.amount ?? 0),
        currency: p.currency ?? 'EUR',
        status: String((p.status ?? 'PENDING')).toLowerCase(),
        method: String((p.method ?? 'BANK_TRANSFER')).toLowerCase(),
        description: p.description ?? undefined,
        proofDocument: p.proofDocument ?? undefined,
        createdAt: p.createdAt ?? new Date().toISOString(),
        verifiedAt: p.verifiedAt ?? undefined,
        verifiedBy: p.verifiedBy?.id ? String(p.verifiedBy.id) : p.verifiedBy ?? undefined,
      }));
      return mapped;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch payments');
    }
  }
);

export const fetchStudentPayments = createAsyncThunk(
  'payment/fetchStudentPayments',
  async (_studentId: string, { rejectWithValue }) => {
    try {
      // authenticated student payments
      const resp = await apiService.get<any>('/api/payments/my');
      const list = Array.isArray(resp?.payments) ? resp.payments : Array.isArray(resp?.data) ? resp.data : [];
      const mapped: Payment[] = list.map((p: any) => ({
        id: String(p.id),
        studentId: String(p.student?.id ?? p.studentId ?? ''),
        amount: Number(p.amount ?? 0),
        currency: p.currency ?? 'EUR',
        status: String((p.status ?? 'PENDING')).toLowerCase(),
        method: String((p.method ?? 'BANK_TRANSFER')).toLowerCase(),
        description: p.description ?? undefined,
        proofDocument: p.proofDocument ?? undefined,
        createdAt: p.createdAt ?? new Date().toISOString(),
        verifiedAt: p.verifiedAt ?? undefined,
        verifiedBy: p.verifiedBy?.id ? String(p.verifiedBy.id) : p.verifiedBy ?? undefined,
      }));
      return mapped;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch student payments');
    }
  }
);

export const fetchMyPaymentDetails = createAsyncThunk(
  'payment/fetchMyPaymentDetails',
  async (_, { rejectWithValue }) => {
    try {
      const resp = await apiService.get<StudentPaymentDetails>(API_ENDPOINTS.PAYMENTS.MY_DETAILS);
      return resp;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch payment details');
    }
  }
);

export const fetchStudentPaymentDetailsForTeacher = createAsyncThunk(
  'payment/fetchStudentPaymentDetailsForTeacher',
  async (studentId: string | number, { rejectWithValue }) => {
    try {
      const resp = await apiService.get<StudentPaymentDetails>(API_ENDPOINTS.PAYMENTS.STUDENT_DETAILS(studentId));
      return resp;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch student payment details');
    }
  }
);

export const uploadPaymentProof = createAsyncThunk(
  'payment/uploadPaymentProof',
  async (
    { 
      file, 
      amount,
      description
    }: { 
      file: File; 
      amount: number;
      description?: string;
    }, 
    { rejectWithValue }
  ) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('amount', String(amount));
      if (description) {
        formData.append('description', description);
      }
      
      const response = await apiService.post<any>(API_ENDPOINTS.PAYMENTS.RECORD_PAYMENT, formData);

      if (response && (response.success || response.data)) {
        return response.data;
      } else {
        return rejectWithValue(response?.message || response?.error || 'Failed to record payment');
      }
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Network error occurred');
    }
  }
);

export const verifyPayment = createAsyncThunk(
  'payment/verifyPayment',
  async (
    { 
      paymentId, 
      status, 
      verifiedBy 
    }: { 
      paymentId: string; 
      status: PaymentStatus; 
      verifiedBy: string 
    }, 
    { rejectWithValue }
  ) => {
    try {
      const qs = new URLSearchParams();
      qs.set('status', String(status).toUpperCase());
      await apiService.post(API_ENDPOINTS.PAYMENTS.VERIFY(paymentId) + `?${qs.toString()}`);
      return { paymentId, status, verifiedBy, verifiedAt: new Date().toISOString() };
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to verify payment');
    }
  }
);

// Initial state
const initialState: PaymentState = {
  payments: [],
  studentPayments: [],
  paymentDetails: null,
  isLoading: false,
  uploadingProof: false,
  error: null,
};

// Payment slice
const paymentSlice = createSlice({
  name: 'payment',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch payments cases
    builder
      .addCase(fetchPayments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPayments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.payments = action.payload;
        state.error = null;
      })
      .addCase(fetchPayments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch student payments cases
    builder
      .addCase(fetchStudentPayments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchStudentPayments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.studentPayments = action.payload;
        state.error = null;
      })
      .addCase(fetchStudentPayments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch my payment details cases
    builder
      .addCase(fetchMyPaymentDetails.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMyPaymentDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.paymentDetails = action.payload;
        state.error = null;
      })
      .addCase(fetchMyPaymentDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch student payment details for teacher cases
    builder
      .addCase(fetchStudentPaymentDetailsForTeacher.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchStudentPaymentDetailsForTeacher.fulfilled, (state, action) => {
        state.isLoading = false;
        state.paymentDetails = action.payload;
        state.error = null;
      })
      .addCase(fetchStudentPaymentDetailsForTeacher.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Upload payment proof cases
    builder
      .addCase(uploadPaymentProof.pending, (state) => {
        state.uploadingProof = true;
        state.error = null;
      })
      .addCase(uploadPaymentProof.fulfilled, (state, action) => {
        state.uploadingProof = false;
        state.payments.push(action.payload);
        state.studentPayments.push(action.payload);
        state.error = null;
      })
      .addCase(uploadPaymentProof.rejected, (state, action) => {
        state.uploadingProof = false;
        state.error = action.payload as string;
      });

    // Verify payment cases
    builder
      .addCase(verifyPayment.fulfilled, (state, action) => {
        const { paymentId, status, verifiedBy, verifiedAt } = action.payload;
        
        // Update in main payments array
        const payment = state.payments.find(p => p.id === paymentId);
        if (payment) {
          payment.status = status;
          payment.verifiedBy = verifiedBy;
          payment.verifiedAt = verifiedAt;
        }
        
        // Update in student payments array
        const studentPayment = state.studentPayments.find(p => p.id === paymentId);
        if (studentPayment) {
          studentPayment.status = status;
          studentPayment.verifiedBy = verifiedBy;
          studentPayment.verifiedAt = verifiedAt;
        }
      });
  },
});

export const { clearError } = paymentSlice.actions;
export default paymentSlice.reducer;